import { ReactElement, useLayoutEffect, useRef, useState } from "react";
import { RowComponentProps } from "react-window";
import { useRecoilValue } from "recoil";

import { usePageContext } from "@ryde/redux";
import {
  activeViewIdAtomFamily,
  buildGroupedTableRowRemeasureSignature,
  DATA_E2E_KEY,
  draggingBetweenRidesGridIdSelector,
  filterCriteriaSF,
  gridExpandedAF,
  GROUPED_TABLE_WRAPPER_ROW_HEIGHT_REFRESH_MS,
  IGroupedTableListItemData,
  nearByFilterAtomFamily,
  nearbyFilteringRideIdAtom,
  produceButtonsPageViewModeAtom,
  showSelectedOnlyRowsAtomFamily,
  starredDisplayModeAtomFamily,
  starredTableRowsIdsAtomFamily,
  TABLE_COLLAPSED_HEIGHT,
  tableFiltersSchemaAtomFamily,
  tableItemPaginationAF,
  tableRowsAtomFamily,
  tableSearchAF,
  userEditOnlyDisplayModeAtomFamily,
} from "@ryde/shared";

import { TableItem } from "./table.item";

export const TableWrapperWindowRow = ({
  index,
  style,
  groupValues,
  setSize,
  layoutSignature,
}: RowComponentProps<IGroupedTableListItemData>): ReactElement => {
  const groupingValue = groupValues[index];
  const [collapsed, setCollapsed] = useState(false);
  const rowRef = useRef<HTMLDivElement>(null);
  const { entity } = usePageContext();
  const filterCriteria = useRecoilValue(filterCriteriaSF(entity));
  const filtersSchema = useRecoilValue(tableFiltersSchemaAtomFamily(entity));
  const searchBoxText = useRecoilValue(tableSearchAF(entity));
  const tableItemPagination = useRecoilValue(
    tableItemPaginationAF([groupingValue ?? "", entity]),
  );
  const gridExpandedCollapse = useRecoilValue(gridExpandedAF(entity));
  const starredOnly = useRecoilValue(starredDisplayModeAtomFamily(entity));
  const userEditedMode = useRecoilValue(
    userEditOnlyDisplayModeAtomFamily(entity),
  );
  const isDraggingBetweenRides = useRecoilValue(
    draggingBetweenRidesGridIdSelector,
  );
  const produceButtonPageView = useRecoilValue(produceButtonsPageViewModeAtom);
  const starredIds = useRecoilValue(starredTableRowsIdsAtomFamily(entity));

  const activeViewId = useRecoilValue(activeViewIdAtomFamily(entity));
  const tableRows = useRecoilValue(tableRowsAtomFamily(entity));
  const nearbyFilteringRideId = useRecoilValue(nearbyFilteringRideIdAtom);
  const nearByFilterIsActive = useRecoilValue(nearByFilterAtomFamily(entity));
  const showSelectedOnlyRowsFilterIsActive = useRecoilValue(
    showSelectedOnlyRowsAtomFamily(entity),
  );

  const rowRemeasureSignature = buildGroupedTableRowRemeasureSignature({
    layoutSignature,
    tableRowsLength: tableRows?.length,
    groupingValue,
    collapsed,
    nearbyFilteringRideId,
    nearByFilterIsActive,
    isDraggingBetweenRides,
    starredOnly,
    userEditedMode,
    produceButtonPageView,
    searchBoxText,
    activeViewId,
    showSelectedOnlyRowsFilterIsActive,
    tableItemPagination,
    gridExpandedCollapse,
    filterCriteria,
    filtersSchema,
    starredIds,
  });

  useLayoutEffect(() => {
    const node = rowRef.current;
    if (!node) {
      return;
    }

    const reportHeight = (): void => {
      const measuredHeight = collapsed
        ? TABLE_COLLAPSED_HEIGHT
        : Math.round(node.getBoundingClientRect().height);

      if (measuredHeight > 0) {
        setSize(index, measuredHeight);
      }
    };

    reportHeight();

    const settleTimeoutId = setTimeout(
      reportHeight,
      GROUPED_TABLE_WRAPPER_ROW_HEIGHT_REFRESH_MS,
    );

    const resizeObserver = new ResizeObserver(reportHeight);
    resizeObserver.observe(node);

    return () => {
      clearTimeout(settleTimeoutId);
      resizeObserver.disconnect();
    };
  }, [setSize, index, collapsed, rowRemeasureSignature]);

  return (
    <div style={style}>
      <div {...{ [DATA_E2E_KEY]: "table-item-wrapper" }} ref={rowRef}>
        <TableItem
          tableIndex={index}
          groupingValue={groupingValue}
          collapsed={collapsed}
          setCollapsed={setCollapsed}
        />
      </div>
    </div>
  );
};
